import { useNavigate } from 'react-router-dom'
import { Box, TextField, Typography } from '@mui/material'
import { always, compose, concat, groupBy, mergeDeepWith } from 'ramda'
import { useAppSelector } from 'src/store/hooks'
import {
  selectCoursesGroupedBySection,
  selectSectionsSorted,
  selectClassesGroupedByCourseID,
  selectCourses,
} from 'src/v3/self-paced/self-paced.slice'
import { ReactElement, useState } from 'react'
import useSession from 'src/authentication/hooks/use-session'
import withConfirm from 'src/components/common/with-confirm'
import { Course } from 'src/v3/core/types'
import { IncorrectIcon, SearchIcon } from 'src/v3/core/icons'
import theme from 'src/theme'
import { onCourseImageError } from 'src/v3/self-paced/utils/course-thumbnail'

export default (): ReactElement => {
  let navigate = useNavigate()
  let session = useSession()

  let sections = useAppSelector(selectSectionsSorted)
  let courses = useAppSelector(selectCoursesGroupedBySection)
  let allCourses = useAppSelector(selectCourses)
  let classesGroupdByCourseID = useAppSelector(selectClassesGroupedByCourseID)

  let [confirmStatus, setConfirmStatus] = useState<'Closed' | 'Open'>('Closed')
  let [searchValue, setSearchValue] = useState<string>('')

  let ConfirmUpgrade = withConfirm(
    'Upgrade to Full Access',
    'IMPORTANT: You once you pay for full access, you will need to log out of Studio and back in to access all of the content.',
    confirmStatus === 'Open',
    compose(setConfirmStatus, always('Closed') as any),
    () => {
      Promise.resolve().then(() =>
        navigate(
          `/subscribe-boundtree?email=${session?.emailAddress
            .toLowerCase()
            .trim()}`,
        ),
      )
    },
  )

  let filteredCourses = allCourses.filter(
    (course) =>
      course.name.toLowerCase().includes(searchValue) ||
      course.displayName.toLowerCase().includes(searchValue) ||
      classesGroupdByCourseID[course.id]?.some(
        (cl) =>
          cl.name.toLowerCase().includes(searchValue) ||
          cl.description.toLowerCase().includes(searchValue),
      ),
  )

  let filteredSections = sections.filter((section) =>
    courses[section.id]?.some((course) =>
      filteredCourses.some((fc) => fc.id === course.id),
    ),
  )

  let filteredCoursesBySection = groupBy((c: Course) => c.sectionID)(
    filteredCourses,
  )

  return (
    <>
      <Box sx={{ mt: 5, p: 2 }}>
        <TextField
          onChange={(e) => setSearchValue(e.target.value.toLowerCase())}
          label="Search Courses"
          fullWidth
          slotProps={{
            input: { endAdornment: <SearchIcon /> },
          }}
        />

        {filteredSections.map((section) => (
          <div className="mt-4" key={section.id}>
            <div className="flex">
              <Typography variant="h5" className="py-2 ml-2">
                {section.name}
              </Typography>
            </div>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, 300px)',
                gap: 2,
                transition: 'all 300ms ease-in-out',
              }}
            >
              {mergeDeepWith(concat, [], filteredCoursesBySection)?.[
                section.id
              ]?.map((item: any) => (
                <Box
                  key={item.id}
                  sx={{
                    position: 'relative',
                    borderRadius: 3,
                    height: 170,
                    transition: 'transform 300ms ease-in-out',
                    '&:hover': {
                      border: `2px solid ${theme.palette.primary.main}`,
                      transform: 'scale(1.1)',
                      zIndex: 2,
                    },
                  }}
                >
                  <img
                    onClick={() =>
                      navigate(
                        `/self-paced/course/${
                          allCourses.find((x) => x.id === item.id)?.id
                        }`,
                      )
                    }
                    style={{
                      cursor: 'pointer',
                      width: '100%',
                      height: '100%',
                      borderRadius: '10px',
                    }}
                    src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/${item.imageName}`}
                    onError={(event) =>
                      onCourseImageError(event, item.imageName)
                    }
                  />
                </Box>
              ))}
            </Box>
          </div>
        ))}
        <ConfirmUpgrade />
        {!filteredCourses.length && (
          <Box
            sx={{
              mt: 1,
              ml: '2px',
              display: 'flex',
              gap: '3px',
              alignItems: 'center',
            }}
          >
            <IncorrectIcon color="error" />
            <Typography color="text.primary" sx={{ display: 'inline-block' }}>
              No courses matched the provided search.
            </Typography>
          </Box>
        )}
      </Box>
    </>
  )
}
